import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { LinearCustomer, LinearCustomerLinkRow } from '@/types/linearClient'
import { clientDashboardKeys } from './clientDashboards'
import { clientMeetingKeys } from './clientMeetings'

export const linearCustomerKeys = {
  links: ['linear-customer-links'] as const,
  customers: ['linear-customer-links', 'customers'] as const,
}

export interface SavedLink {
  link: LinearCustomerLinkRow
  warning?: string
}

export function useLinearCustomerLinks() {
  return useQuery({
    queryKey: linearCustomerKeys.links,
    queryFn: () => api<{ data: LinearCustomerLinkRow[]; warning?: string }>('/linear-customer-links'),
  })
}

export function useLinearCustomers(enabled: boolean) {
  return useQuery({
    queryKey: linearCustomerKeys.customers,
    queryFn: () => api<{ data: LinearCustomer[] }>('/linear-customer-links/customers'),
    enabled,
  })
}

// A link change moves issues between clients, so every client's Linear issues refetch too.
function useInvalidateLinks() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: linearCustomerKeys.links }),
      queryClient.invalidateQueries({ queryKey: [...clientDashboardKeys.all, 'linear-issues'] }),
      queryClient.invalidateQueries({ queryKey: clientMeetingKeys.mismatches() }),
    ])
}

export function useSaveLinearCustomerLink() {
  const invalidate = useInvalidateLinks()
  return useMutation({
    mutationFn: ({ _id, companyId, linearCustomerId }: { _id?: string; companyId: string; linearCustomerId: string }) =>
      _id
        ? api<SavedLink>(`/linear-customer-links/${_id}`, { method: 'PATCH', body: { companyId, linearCustomerId } })
        : api<SavedLink>('/linear-customer-links', { method: 'POST', body: { companyId, linearCustomerId } }),
    onSuccess: invalidate,
  })
}

export function useRemoveLinearCustomerLink() {
  const invalidate = useInvalidateLinks()
  return useMutation({
    mutationFn: (id: string) => api(`/linear-customer-links/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })
}
